import React from 'react';

// Skin tone mapping
export const SKIN_TONES = [
  { name: 'Fair', value: '#ffe4e6' },
  { name: 'Light', value: '#fed7aa' },
  { name: 'Olive', value: '#d97706' },
  { name: 'Dark', value: '#7c2d12' }
];

// Hair style mapping
export const HAIR_STYLES = [
  { name: '숏컷', value: 'short' },
  { name: '단발', value: 'bob' },
  { name: '롱헤어', value: 'long' },
  { name: '뽀글이', value: 'curly' },
  { name: '대머리', value: 'bald' }
];

// Hair color mapping
export const HAIR_COLORS = [
  { name: '검은색', value: '#1e293b' },
  { name: '갈색', value: '#78350f' },
  { name: '노란색', value: '#eab308' },
  { name: '빨간색', value: '#991b1b' }
];

// Eye shape mapping
export const EYE_SHAPES = [
  { name: '웃는 눈', value: 'smiling' },
  { name: '날카로운 눈', value: 'sharp' },
  { name: '동그란 눈', value: 'round' },
  { name: '졸린 눈', value: 'sleepy' }
];

// Eye size mapping
export const EYE_SIZES = [
  { name: '작음', value: 'small' },
  { name: '중간', value: 'medium' },
  { name: '큼', value: 'large' }
];

// Face shape mapping
export const FACE_SHAPES = [
  { name: '계란형', value: 'oval' },
  { name: '둥근형', value: 'round' },
  { name: '각진형', value: 'square' },
  { name: '하트형', value: 'heart' }
];

const Avatar = ({ config, size = 48, className = '', name = '' }) => {
  // Normalize size to number
  let numericSize = 48;
  if (typeof size === 'number') {
    numericSize = size;
  } else if (typeof size === 'string') {
    const sizeMap = {
      xs: 24,
      sm: 32,
      md: 44,
      lg: 56,
      xl: 80,
      '2xl': 96
    };
    numericSize = sizeMap[size] || parseInt(size, 10) || 48;
  }

  // Default values if config is missing attributes
  const defaults = {
    hairStyle: 'short',
    hairColor: '#1e293b',
    eyesShape: 'round',
    eyeSize: 'medium',
    faceShape: 'oval',
    skinTone: '#fed7aa'
  };

  // Safe parsing if config is a JSON string
  let parsedConfig = {};
  let imageUrl = null;

  if (typeof config === 'string') {
    if (config.startsWith('data:image/') || config.startsWith('http://') || config.startsWith('https://')) {
      imageUrl = config;
    } else {
      try {
        parsedConfig = JSON.parse(config);
      } catch (e) {
        if (config.length > 30) {
          imageUrl = config;
        } else {
          parsedConfig = {};
        }
      }
    }
  } else if (config && typeof config === 'object') {
    parsedConfig = config;
  }

  if (parsedConfig && parsedConfig.imageUrl) {
    imageUrl = parsedConfig.imageUrl;
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name || "Avatar"}
        style={{ width: numericSize, height: numericSize, minWidth: numericSize, minHeight: numericSize }}
        className={`rounded-full object-cover flex-shrink-0 border border-slate-200/50 ${className}`}
      />
    );
  }

  const { hairStyle, hairColor, eyesShape, eyeSize, faceShape, skinTone } = {
    ...defaults,
    ...parsedConfig
  };

  // SVG drawing logic based on parameters

  // 1. Face Shape paths
  let facePath = "M 30,50 Q 50,85 70,50 Q 75,30 50,30 Q 25,30 30,50 Z"; // oval default
  if (faceShape === 'round') {
    facePath = "M 25,50 A 25,25 0 1,0 75,50 A 25,25 0 1,0 25,50 Z";
  } else if (faceShape === 'square') {
    facePath = "M 30,30 L 70,30 L 70,70 Q 50,80 30,70 Z";
  } else if (faceShape === 'heart') {
    facePath = "M 30,35 Q 50,20 70,35 Q 75,55 50,85 Q 25,55 30,35 Z";
  }

  // 2. Eye Size scale
  let eyeScale = 1.0;
  if (eyeSize === 'small') eyeScale = 0.65;
  if (eyeSize === 'large') eyeScale = 1.4;

  // 3. Eyes rendering
  const renderEyes = () => {
    if (eyesShape === 'smiling') {
      return (
        <g stroke="#0f172a" strokeWidth="2.5" fill="none" strokeLinecap="round">
          {/* Smiling arcs */}
          <path d={`M ${50 - 10 * eyeScale} 48 Q ${50 - 6 * eyeScale} ${48 - 4 * eyeScale} ${50 - 2 * eyeScale} 48`} transform={`scale(${eyeScale}) translate(${50 * (1 - eyeScale) / eyeScale}, ${48 * (1 - eyeScale) / eyeScale})`} />
          <path d={`M ${50 + 2 * eyeScale} 48 Q ${50 + 6 * eyeScale} ${48 - 4 * eyeScale} ${50 + 10 * eyeScale} 48`} transform={`scale(${eyeScale}) translate(${50 * (1 - eyeScale) / eyeScale}, ${48 * (1 - eyeScale) / eyeScale})`} />
        </g>
      );
    }
    if (eyesShape === 'sharp') {
      return (
        <g fill="#0f172a">
          {/* Angry / Sharp tilted almonds */}
          <path d={`M ${48 - 8 * eyeScale} 48 L ${48 - 2 * eyeScale} 46 L ${48 - 4 * eyeScale} 50 Z`} />
          <path d={`M ${52 + 8 * eyeScale} 48 L ${52 + 2 * eyeScale} 46 L ${52 + 4 * eyeScale} 50 Z`} />
        </g>
      );
    }
    if (eyesShape === 'sleepy') {
      return (
        <g stroke="#0f172a" strokeWidth="2.5" fill="none" strokeLinecap="round">
          {/* Flat horizontal lines / downward arcs */}
          <path d={`M ${50 - 9 * eyeScale} 48 L ${50 - 3 * eyeScale} 48`} />
          <path d={`M ${50 + 3 * eyeScale} 48 L ${50 + 9 * eyeScale} 48`} />
        </g>
      );
    }
    // Default round eyes
    return (
      <g fill="#0f172a">
        <circle cx={50 - 7 * eyeScale} cy="48" r={3 * eyeScale} />
        <circle cx={50 + 7 * eyeScale} cy="48" r={3 * eyeScale} />
      </g>
    );
  };

  // 4. Hair Styles rendering
  const renderHair = () => {
    if (hairStyle === 'bald') return null;

    if (hairStyle === 'long') {
      return (
        <path 
          d="M 25,40 C 20,40 18,60 18,75 C 18,78 22,78 24,75 C 26,70 28,50 30,45 L 70,45 C 72,50 74,70 76,75 C 78,78 82,78 82,75 C 82,60 80,40 75,40 Q 50,15 25,40 Z" 
          fill={hairColor} 
        />
      );
    }
    if (hairStyle === 'bob') {
      return (
        <path 
          d="M 24,45 C 20,45 20,62 21,65 C 23,67 27,65 28,60 C 29,55 30,45 32,40 L 68,40 C 70,45 71,55 72,60 C 73,65 77,67 79,65 C 80,62 80,45 76,45 Q 50,18 24,45 Z" 
          fill={hairColor} 
        />
      );
    }
    if (hairStyle === 'curly') {
      return (
        <g fill={hairColor}>
          {/* Group of overlapping curly puffs */}
          <circle cx="50" cy="22" r="14" />
          <circle cx="38" cy="26" r="12" />
          <circle cx="62" cy="26" r="12" />
          <circle cx="28" cy="34" r="11" />
          <circle cx="72" cy="34" r="11" />
          <circle cx="26" cy="44" r="10" />
          <circle cx="74" cy="44" r="10" />
        </g>
      );
    }
    // Default: short cut hair
    return (
      <path 
        d="M 28,36 C 28,26 40,20 50,20 C 60,20 72,26 72,36 C 74,38 75,34 74,31 C 71,21 61,16 50,16 C 39,16 29,21 26,31 C 25,34 26,38 28,36 Z" 
        fill={hairColor} 
      />
    );
  };

  return (
    <svg 
      viewBox="0 0 100 100" 
      width={numericSize} 
      height={numericSize} 
      style={{ width: numericSize, height: numericSize, minWidth: numericSize, minHeight: numericSize }}
      className={`rounded-full shadow-xs bg-slate-100 flex-shrink-0 border border-slate-200/50 ${className}`}
    >
      {/* Background / Neck */}
      <rect x="42" y="70" width="16" height="15" rx="8" fill={skinTone} opacity="0.85" />
      {/* Shirt */}
      <path d="M 30,85 C 30,80 40,78 50,78 C 60,78 70,80 70,85 Z" fill="#6366f1" />
      <path d="M 45,78 L 50,83 L 55,78 Z" fill={skinTone} /> {/* collar cutout */}
      
      {/* Face Base */}
      <path d={facePath} fill={skinTone} />

      {/* Eyes */}
      {renderEyes()}

      {/* Smile / Mouth */}
      <path d="M 45,63 Q 50,68 55,63" stroke="#0f172a" strokeWidth="2.5" fill="none" strokeLinecap="round" />

      {/* Hair (renders on top of head) */}
      {renderHair()}
    </svg>
  );
};

export default Avatar;
